import { Injectable } from '@angular/core';
import { mathjax } from '@mathjax/src/mjs/mathjax.js';
import { TeX } from '@mathjax/src/mjs/input/tex.js';
import { browserAdaptor } from '@mathjax/src/mjs/adaptors/browserAdaptor.js';
import { RegisterHTMLHandler } from '@mathjax/src/mjs/handlers/html.js';
import { STATE } from '@mathjax/src/mjs/core/MathItem.js';
import { SVG } from '@mathjax/src/mjs/output/svg.js';
import { MathDocument } from '@mathjax/src/mjs/core/MathDocument.js';
import { MathJaxNewcmFont } from '@mathjax/mathjax-newcm-font/mjs/svg.js';
import { MathJaxBbmFontExtension } from '@mathjax/mathjax-bbm-font-extension/mjs/svg.js';
import { MathJaxBboldxFontExtension } from '@mathjax/mathjax-bboldx-font-extension/mjs/svg.js';
import { MathJaxMhchemFontExtension } from '@mathjax/mathjax-mhchem-font-extension/mjs/svg.js';
import { MathJaxDsfontFontExtension } from '@mathjax/mathjax-dsfont-font-extension/mjs/svg.js';
import { ALL_TEX_PACKAGES } from './math-extensions';
import { HTMLAdaptor } from '@mathjax/src/js/adaptors/HTMLAdaptor.js';

@Injectable({
  providedIn: 'root',
})
export class MathService {
  private adaptor: HTMLAdaptor<any, any, any> = browserAdaptor();
  private mathDocument: MathDocument<any, any, any> | null = null;

  constructor() {
    RegisterHTMLHandler(this.adaptor);

    MathJaxNewcmFont.addExtension(MathJaxBbmFontExtension);
    MathJaxNewcmFont.addExtension(MathJaxBboldxFontExtension);
    MathJaxNewcmFont.addExtension(MathJaxMhchemFontExtension);
    MathJaxNewcmFont.addExtension(MathJaxDsfontFontExtension);

    this.mathDocument = mathjax.document('<html></html>', {
      compileError(doc: any, math: any, err: any): any {
        doc.compileError(math, err);
      },
      typesetError(doc: any, math: any, err: any): any {
        doc.typesetError(math, err);
      },
      InputJax: [
        new TeX({
          packages: Array.from(ALL_TEX_PACKAGES),
          autoload: {
            boldsymbol: ['boldsymbol'],
            extpfeil: [
              'xtwoheadrightarrow',
              'xtwoheadleftarrow',
              'xmapsto',
              'xlongequal',
              'xtofrom',
              'Newextarrow',
            ],
            mhchem: ['ce', 'pu'],
          },
          inlineMath: [['\\(', '\\)']],
          displayMath: [
            ['$$', '$$'],
            ['\\[', '\\]'],
          ],
          processEscapes: true,
          processEnvironments: true,
          processRefs: true,
          numberPattern: /^(?:[0-9]+(?:\{,\}[0-9]{3})*(?:\.[0-9]*)?|\.[0-9]+)/,
          tags: 'none',
          tagSide: 'right',
          tagIndent: '0.8em',
          useLabelIds: true,
          maxMacros: 1000,
          maxBuffer: 5 * 1024,
          baseURL:
            document.getElementsByTagName('base').length === 0
              ? ''
              : String(document.location).replace(/#.*$/, ''),
          formatError: (jax: any, err: any) => {
            throw err;
          },
        }),
      ],
      OutputJax: new SVG({
        scale: 1,
        minScale: 0.5,
        mtextInheritFont: false,
        merrorInheritFont: true,
        mathmlSpacing: false,
        skipAttributes: {},
        exFactor: 0.5,
        displayAlign: 'center',
        displayIndent: '0',
        fontCache: 'local',
        fontData: MathJaxNewcmFont,
        localID: null,
      }),
    });
  }

  public async renderEquation(content: string): Promise<{
    errors: { id: string; message: string }[];
    node: string | null;
  }> {
    if (!this.mathDocument) {
      return {
        errors: [{ id: 'MathJaxNotInitialized', message: 'MathJax document is not initialized' }],
        node: null,
      };
    }

    try {
      const node = await this.mathDocument.convertPromise(content, { end: STATE.TYPESET });
      const svgMarkup = this.adaptor.outerHTML(node);

      const parser = new DOMParser();
      const doc = parser.parseFromString(svgMarkup, 'text/html');
      const svgElement = doc.querySelector('svg');

      if (!svgElement) {
        return {
          errors: [{ id: 'SVGNotFound', message: 'SVG element not found in rendered output' }],
          node: null,
        };
      }

      // Check for embedded MathJax errors (merror elements)
      const merrorElements = svgElement.querySelectorAll('[data-mml-node="merror"]');
      const errors: { id: string; message: string }[] = [];
      
      if (merrorElements.length > 0) {
        merrorElements.forEach((merror) => {
          const errorMsg = merror.getAttribute('data-mjx-error') || merror.getAttribute('title') || 'Unknown error';
          errors.push({ id: 'MathJaxError', message: errorMsg });
        });
      }

      return { errors, node: svgElement.outerHTML };
    } catch (err: unknown) {
      console.error('Could not render equation', err);
      const errorObj = err as any;
      const id = errorObj?.id ?? 'Error';
      const message = errorObj?.message ?? String(err);
      return { errors: [{ id, message }], node: null };
    } finally {
      this.mathDocument.clear();
    }
  }
}
